/* Variante B · Rollen-Matrix mit Human-Labels + editierbaren Toggle-Zellen.
 * Owner kann Zellen togglen (außer Owner-Spalte, die ist unveränderlich).
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, ShieldAlert, Shield, Eye, RefreshCw, History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_META = {
  owner:     { label: "Owner",     icon: ShieldCheck, color: "text-amber-600",   order: 4 },
  admin:     { label: "Admin",     icon: ShieldAlert, color: "text-purple-600",  order: 3 },
  moderator: { label: "Moderator", icon: Shield,      color: "text-emerald-600", order: 2 },
  support:   { label: "Support",   icon: Eye,         color: "text-blue-600",    order: 1 },
};

export default function AdminPermissionsPanel() {
  const { user: authUser } = useAuth();
  const isOwner = authUser?.role === "owner";
  const [matrix, setMatrix] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const [m, a] = await Promise.all([
        api.get("/admin/permissions"),
        api.get("/admin/role-audit-log?limit=50"),
      ]);
      setMatrix(m.data);
      setAudit(a.data.entries || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const rolesAsc = matrix ? [...matrix.roles].sort((a, b) => (ROLE_META[a]?.order || 0) - (ROLE_META[b]?.order || 0)) : [];

  const groupedByCategory = useMemo(() => {
    if (!matrix) return {};
    const out = {};
    matrix.permissions.forEach((p) => {
      if (!out[p.category]) out[p.category] = [];
      out[p.category].push(p);
    });
    return out;
  }, [matrix]);

  const toggleCell = async (permission, role, currentAllowed) => {
    if (!isOwner || role === "owner") return;
    const key = `${permission}::${role}`;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.patch("/admin/permissions", {
        permission,
        role,
        allowed: !currentAllowed,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div data-testid="admin-permissions-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Rollen & Berechtigungen</h2>
          <p className="text-xs text-[#666] mt-0.5">
            {isOwner
              ? "Klicke auf eine Zelle, um die Berechtigung an-/auszuschalten. Owner-Spalte ist fixiert."
              : "Matrix aller Admin-Rollen. Bearbeitung nur durch den System-Owner."}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="admin-permissions-refresh"
          className="h-9 px-4 rounded-full border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#FAFAFA] flex items-center gap-1"
        >
          <RefreshCw size={12} /> Aktualisieren
        </button>
      </div>

      {loading && <p className="text-sm text-[#999]">Lade …</p>}
      {error && <p className="text-sm text-red-500" data-testid="admin-permissions-error">{error}</p>}

      {matrix && Object.keys(groupedByCategory).sort().map((cat) => (
        <div key={cat} data-testid={`perm-category-${cat}`} className="rounded-2xl border border-[#E5E5E5] overflow-hidden">
          <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A0A0A]">{cat}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-2.5 font-semibold text-[#0A0A0A] w-1/2">Aktion</th>
                  {rolesAsc.map((r) => {
                    const meta = ROLE_META[r];
                    const Icon = meta?.icon;
                    return (
                      <th key={r} className="text-center px-3 py-2.5 font-semibold uppercase tracking-wider text-[10px]" data-testid={`role-col-${r}`}>
                        <div className="inline-flex items-center gap-1">
                          {Icon && <Icon size={11} className={meta.color} />}
                          {meta?.label || r}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0F0F0]">
                {groupedByCategory[cat].map((p) => (
                  <tr key={p.key} data-testid={`perm-row-${p.key}`} className="hover:bg-[#FAFAFA]">
                    <td className="px-4 py-2.5">
                      <p className="text-sm text-[#0A0A0A]">{p.label}</p>
                      <code className="text-[9px] font-mono text-[#999]">{p.key}</code>
                    </td>
                    {rolesAsc.map((r) => {
                      const active = p.allowed_roles.includes(r);
                      const isFixed = r === "owner";
                      const busyKey = `${p.key}::${r}`;
                      const busy = saving[busyKey];
                      const clickable = isOwner && !isFixed;
                      return (
                        <td key={r} className="text-center px-3 py-2">
                          {busy ? (
                            <Loader2 size={14} className="animate-spin text-[#999] inline-block" />
                          ) : (
                            <button
                              type="button"
                              disabled={!clickable}
                              onClick={() => toggleCell(p.key, r, active)}
                              data-testid={`perm-cell-${p.key}-${r}`}
                              className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                                active
                                  ? isFixed
                                    ? "bg-amber-100 text-amber-600 cursor-default"
                                    : clickable ? "bg-emerald-100 text-emerald-600 hover:bg-emerald-200 cursor-pointer" : "bg-emerald-100 text-emerald-600 cursor-default"
                                  : clickable ? "bg-[#F5F5F5] text-[#999] hover:bg-[#EEEEEE] cursor-pointer" : "text-[#DDD] cursor-default"
                              }`}
                              title={clickable ? (active ? "Deaktivieren" : "Aktivieren") : ""}
                            >
                              {active ? "✓" : "—"}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div>
        <div className="flex items-center gap-2 mb-2">
          <History size={14} className="text-[#0A0A0A]" />
          <h3 className="text-sm font-bold text-[#0A0A0A]">Audit-Log · Letzte Änderungen</h3>
        </div>
        <div className="rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {audit.length === 0 ? (
            <p className="p-4 text-xs text-[#999] text-center" data-testid="audit-empty">Noch keine Änderungen protokolliert.</p>
          ) : (
            <ul className="divide-y divide-[#F0F0F0]">
              {audit.map((e) => (
                <li key={e.audit_id} data-testid={`audit-entry-${e.audit_id}`} className="px-4 py-2.5 text-xs">
                  <p className="text-[#0A0A0A]">
                    <span className="font-mono text-[10px] text-[#999]">{new Date(e.created_at).toLocaleString("de-DE")}</span>
                    {" · "}
                    <span className="font-semibold">{e.actor_email}</span>
                    {" — "}
                    <span className="text-[#666]">{e.action}</span>
                    {e.target_email && <> · Ziel: <span className="font-semibold">{e.target_email}</span></>}
                  </p>
                  {(e.old_value !== undefined || e.new_value !== undefined) && (
                    <p className="text-[11px] text-[#666] mt-0.5">
                      {e.old_value ? <><code className="text-[10px]">{String(e.old_value)}</code> → </> : null}
                      <code className="text-[10px] text-[#0A0A0A] font-bold">{String(e.new_value || "—")}</code>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
